import { useEffect, useState, useRef } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";

/**
 * Props expected (safe if missing):
 * - founderId (required)
 * - evaluation: { evaluationId, status }
 * - mode: "review" | "view"
 */
function ReadOnlyClaims({ founderId, evaluation, mode }) {
  const [grouped, setGrouped] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [evidences, setEvidences] = useState({});

  const [claimRatings, setClaimRatings] = useState({});
  const [evidenceRatings, setEvidenceRatings] = useState({});

  const isReviewMode = mode === "review";
  const isLocked = evaluation?.status === "submitted";
  const evaluationId = evaluation?.evaluationId || null;

  // 🔒 Deduplication guards for analytics
  const viewedClaims = useRef(new Set());
  const openedEvidence = useRef(new Set());

  /* =========================
     LOAD CLAIMS
  ========================= */
  useEffect(() => {
    const loadClaims = async () => {
      const q = query(
        collection(db, "claims"),
        where("userId", "==", founderId)
      );
      const snap = await getDocs(q);

      const groupedClaims = {};
      snap.docs.forEach((d) => {
        const c = { id: d.id, ...d.data() };
        const g = c.group || "Other";
        if (!groupedClaims[g]) groupedClaims[g] = [];
        groupedClaims[g].push(c);

        // 📊 Analytics: claim view (once)
        if (!viewedClaims.current.has(c.id)) {
          viewedClaims.current.add(c.id);
          try {
            addDoc(collection(db, "analytics"), {
              ownerId: founderId,
              eventType: "claim_view",
              source: "evaluator",
              entityType: "claim",
              entityId: c.id,
              timestamp: serverTimestamp(),
              userAgent: navigator.userAgent,
            });
          } catch {
            // silent fail
          }
        }
      });

      setGrouped(groupedClaims);

      const col = {};
      Object.keys(groupedClaims).forEach((g) => (col[g] = false));
      setCollapsed(col);
    };

    loadClaims();
  }, [founderId]);

  /* =========================
     LOAD EVIDENCES
  ========================= */
  useEffect(() => {
    const loadEvidences = async () => {
      const q = query(
        collection(db, "evidences"),
        where("userId", "==", founderId)
      );
      const snap = await getDocs(q);

      const ev = {};
      snap.docs.forEach((d) => {
        const e = { id: d.id, ...d.data() };
        if (!ev[e.claimId]) ev[e.claimId] = [];
        ev[e.claimId].push(e);
      });

      setEvidences(ev);
    };

    loadEvidences();
  }, [founderId]);

  /* =========================
     LOAD EXISTING RATINGS
  ========================= */
  useEffect(() => {
    if (!evaluationId) return;

    const loadRatings = async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, "evaluations"),
            where("__name__", "==", evaluationId)
          )
        );

        if (!snap.empty) {
          const data = snap.docs[0].data();
          setClaimRatings(data.claimRatings || {});
          setEvidenceRatings(data.evidenceRatings || {});
        }
      } catch {
        // silent fail
      }
    };

    loadRatings();
  }, [evaluationId]);

  /* =========================
     SAVE CLAIM RATING
  ========================= */
  const saveClaimRating = async (claimId, rating) => {
    if (!evaluationId || isLocked || !isReviewMode) return;

    const updated = {
      ...claimRatings,
      [claimId]: {
        rating,
        updatedAt: serverTimestamp(),
      },
    };

    setClaimRatings(updated);

    try {
      await updateDoc(doc(db, "evaluations", evaluationId), {
        claimRatings: updated,
        updatedAt: serverTimestamp(),
      });
    } catch {
      // silent fail
    }
  };

  /* =========================
     SAVE EVIDENCE RATING
  ========================= */
  const saveEvidenceRating = async (evidenceId, strength) => {
    if (!evaluationId || isLocked || !isReviewMode) return;

    const updated = {
      ...evidenceRatings,
      [evidenceId]: {
        strength,
        updatedAt: serverTimestamp(),
      },
    };

    setEvidenceRatings(updated);

    try {
      await updateDoc(doc(db, "evaluations", evaluationId), {
        evidenceRatings: updated,
        updatedAt: serverTimestamp(),
      });
    } catch {
      // silent fail
    }
  };

  const handleEvidenceOpen = async (evidenceId) => {
    if (openedEvidence.current.has(evidenceId)) return;
    openedEvidence.current.add(evidenceId);

    try {
      await addDoc(collection(db, "analytics"), {
        ownerId: founderId,
        eventType: "evidence_open",
        source: "evaluator",
        entityType: "evidence",
        entityId: evidenceId,
        timestamp: serverTimestamp(),
        userAgent: navigator.userAgent,
      });
    } catch {
      // silent fail
    }
  };

  /* =========================
     RENDER
  ========================= */
  return (
    <div style={{ marginBottom: 24 }}>
      <h3
        title="Claims are founder assertions extracted from the pitch. You are evaluating credibility, not legality."
      >
        Claims
      </h3>

      {Object.entries(grouped).map(([group, claims]) => (
        <div key={group} style={{ marginBottom: 24 }}>
          <div
            style={{ cursor: "pointer", fontWeight: 600 }}
            onClick={() =>
              setCollapsed((p) => ({ ...p, [group]: !p[group] }))
            }
            title="Expand or collapse this claim category"
          >
            {collapsed[group] ? "▶" : "▼"} {group}
          </div>

          {!collapsed[group] &&
            claims.map((c) => (
              <div
                key={c.id}
                className="card"
                style={{ padding: 16, marginTop: 12 }}
              >
                <strong
                  title="Evaluate whether this claim is clear, reasonable, and adequately supported — not whether it is objectively true."
                >
                  {c.text}
                </strong>

                {/* CLAIM RATING */}
                <div style={{ marginTop: 10 }}>
                  <div
                    style={{ fontSize: 13, marginBottom: 6 }}
                    title="How credible is this claim based on clarity, logic, and available evidence?"
                  >
                    Claim credibility
                  </div>

                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      disabled={!isReviewMode || isLocked || !evaluationId}
                      onClick={() => saveClaimRating(c.id, n)}
                      className={
                        claimRatings[c.id]?.rating === n
                          ? "btn-primary"
                          : "btn-secondary"
                      }
                      style={{ marginRight: 6 }}
                      title={
                        n === 1
                          ? "Very weak: unclear, unsupported, or speculative"
                          : n === 3
                          ? "Moderate: plausible but gaps exist"
                          : n === 5
                          ? "Strong: clear, specific, and well-supported"
                          : "Rate claim credibility"
                      }
                    >
                      {n} ⭐
                    </button>
                  ))}
                </div>

                {/* EVIDENCES */}
                {(evidences[c.id] || []).map((ev) => (
                  <div key={ev.id} style={{ marginTop: 10 }}>
                    <a
                      href={ev.type === "link" ? ev.url : ev.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => handleEvidenceOpen(ev.id)}
                      title="Open evidence provided by the founder"
                    >
                      {ev.type === "link" ? "🔗" : "📄"} {ev.label}
                    </a>

                    {/* EVIDENCE RATING */}
                    <div style={{ marginTop: 6 }}>
                      <span
                        style={{ fontSize: 12, marginRight: 6 }}
                        title="Evaluate how strongly this evidence supports the claim"
                      >
                        Evidence strength:
                      </span>

                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          disabled={!isReviewMode || isLocked || !evaluationId}
                          onClick={() => saveEvidenceRating(ev.id, n)}
                          className={
                            evidenceRatings[ev.id]?.strength === n
                              ? "btn-primary"
                              : "btn-secondary"
                          }
                          style={{ marginRight: 4 }}
                          title={
                            n === 1
                              ? "Weak: irrelevant, outdated, or unverifiable"
                              : n === 3
                              ? "Moderate: partially supports the claim"
                              : n === 5
                              ? "Strong: directly substantiates the claim"
                              : "Rate evidence strength"
                          }
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
        </div>
      ))}
    </div>
  );
}

export default ReadOnlyClaims;
